/* @flow */
import * as React from 'react';
import { IndexRoute, Route, IndexRedirect } from 'react-router';

import { AdminAutoMessages } from 'admin/components/AdminAutoMessages';
import { AdminAutoMessageDetail } from 'admin/components/AdminAutoMessageDetail';
import { AdminUploadedFiles } from 'admin/components/AdminUploadedFiles';
import { AdminAutoMessageSftpCredentials } from 'admin/components/AdminAutoMessageSftpCredentials';
import AdminAdminsRedirect from 'admin/components/AdminAdminsRedirect';
import AdminBillingOverview from 'admin/components/AdminBillingOverview';
import AdminDefaultClassSettings from 'admin/components/AdminDefaultClassSettings';
import AdminGroupsContainer from 'admin/components/AdminGroups';
import AdminHomeView from 'admin/components/AdminHomeView';
import AdminIntegratedToolsView from 'admin/components/AdminIntegratedToolsView';
import AdminOrgAdminsView from 'admin/components/AdminOrgAdminsView';
import AdminOrgApprovalSettings from 'admin/components/AdminOrgApprovalSettings/components/AdminOrgApprovalSettings';
import AdminSchoolAdminsView from 'admin/components/AdminSchoolAdminsView';
import AdminSchools from 'admin/components/AdminSchools';
import AdminSisSync from 'admin/components/AdminSisSync';
import AdminSettingsLayout from 'admin/components/AdminSettingsLayout';
import AdminStatisticsView from 'admin/components/AdminStatisticsView';
import AnnouncementStreamDefaultView from 'chat/components/AnnouncementStream/components/AnnouncementStreamDefaultView';
import AnnouncementStreamView from 'chat/components/AnnouncementStream/components/AnnouncementStreamView';
import BootstrapHandler from 'groups/components/BootstrapHandler';
import ChatDefaultView from 'chat/components/ChatDefaultView';
import ConversationView from 'chat/components/ConversationView';
import CreateGroupHandler from 'groups/components/CreateGroupHandler';
import FeatureSpecificIndexHandler from 'app/components/FeatureSpecificIndexHandler';
import FilesContainer from 'files/containers/FilesContainer';
import GroupContainer from 'groups/containers/GroupContainer';
import { GroupSettingsTab } from 'groups/components/GroupSettingsTab';
import GroupInviteHandler from 'groups/components/GroupInviteHandler';
import GroupMessagesContainer from 'groups/containers/GroupMessagesContainer';
import PendingMemberships from 'groupMembershipRequests/components/PendingMemberships';
import PendingRoles from 'roleRequests/components/PendingRoles';
import GroupPeopleTable from 'groups/components/GroupPeopleTable';
import GroupSettingsHandler from 'groups/components/GroupSettingsHandler';
import GroupsIndexHandler from 'groups/components/GroupsIndexHandler';
import GroupsView from 'groups/components/GroupsView';
import GroupView from 'groups/components/GroupView';
import GroupViewIndex from 'groups/components/GroupViewIndex';
// import FullPageComposerView from 'chat/components/FullPageComposer/FullPageComposerView.jsx';
import PendingGroupContainer from 'groups/containers/PendingGroupContainer';
import JoinGroupHandler from 'groups/components/JoinGroupHandler';
import PhoneCallsTab from 'phoneCalls/components/PhoneCallsTab';
import RequireFeatures from 'lib/RequireFeatures';

const AutoMessagingIndexHandler = props => (
  <FeatureSpecificIndexHandler
    {...props}
    orgPermissionCondition="can_send_auto_message"
    urlPath="settings/organization/auto_messages"
  />
);

export default (
  <Route path="/classes(/)" component={GroupsView} onEnter={GroupsView.onEnter}>
    <IndexRoute component={GroupsIndexHandler} />
    <Route path="start" component={BootstrapHandler} />
    <Route path="join" component={JoinGroupHandler} />
    <Route path="new" component={CreateGroupHandler} />
    <Route path="auto_messaging" component={AutoMessagingIndexHandler} />
    <Route path=":groupCode" component={GroupContainer}>
      {/* Testing route for New Composer - make sure to comment before push */}
      {/* <Route path="compose/(:chatId)" component={FullPageComposerView} /> */}

      <IndexRoute component={GroupViewIndex} />
      <Route components={GroupView}>
        <Route path="messages" component={GroupMessagesContainer}>
          <Route
            path="new(/:memberId)"
            component={ConversationView}
            onEnter={ConversationView.onEnter}
            onLeave={ConversationView.onLeave}
          />
          <Route path="stream(/)">
            <Route path=":uuid" component={AnnouncementStreamView} />
            <IndexRoute
              component={AnnouncementStreamDefaultView}
              onEnter={AnnouncementStreamDefaultView.onEnter}
            />
          </Route>
          <Route path=":chatId" component={ConversationView} onEnter={ConversationView.onEnter} />
          <IndexRoute component={ChatDefaultView} onEnter={ChatDefaultView.onEnter} />
        </Route>
        <Route path="invite" component={GroupInviteHandler} />
        <Route path="settings" component={GroupSettingsHandler} />
        <Route path="files" component={FilesContainer} />
        <Route path="class_settings" component={GroupSettingsTab} />
        <Route path="people">
          <IndexRoute component={GroupPeopleTable} />
          <Route path="pending_memberships" component={PendingMemberships} />
          <Route path="pending_roles" component={PendingRoles} />
        </Route>
        <Route path="calls" component={PhoneCallsTab} />
        <Route path="classes" component={AdminGroupsContainer} />
        <Route path="home" component={AdminHomeView} />
        <Route path="statistics" component={AdminStatisticsView} />
        <Route path="schools" component={AdminSchools} />
        <Route path="sis_sync" component={AdminSisSync} />
        <Route path="settings" component={AdminSettingsLayout}>
          <IndexRedirect to="settings/organization" />
          <Route path="organization">
            {/* not sure how i can set the default page to load based on experiment? */}
            <IndexRedirect to="default_class_settings" />
            <Route path="organization_settings" component={AdminOrgApprovalSettings} />
            <Route
              onEnter={RequireFeatures(
                ['org_level_comms_controls.web.nov-19'],
                (nextState, replaceWith) => {
                  replaceWith('/classes');
                }
              )}>
              <Route path="default_class_settings" component={AdminDefaultClassSettings} />
            </Route>
            <Route path="administrators">
              <IndexRoute component={AdminAdminsRedirect} />
              <Route path="district" component={AdminOrgAdminsView} />
              <Route path="school" component={AdminSchoolAdminsView} />
              <Route path="*" component={AdminAdminsRedirect} />
            </Route>
            <Route path="billing" component={AdminBillingOverview} />
            <Route path="integrated_tools" component={AdminIntegratedToolsView} />
            <Route
              onEnter={RequireFeatures(
                ['2018.nov.web.auto_messaging'],
                (nextState, replaceWith) => {
                  replaceWith('/classes');
                }
              )}>
              <Route path="auto_messages">
                <IndexRoute component={AdminAutoMessages} />
                <Route path=":uuid" component={AdminAutoMessageDetail} />
              </Route>
              <Route path="uploaded_files" component={AdminUploadedFiles} />
              <Route path="sftp_credentials" component={AdminAutoMessageSftpCredentials} />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="pending" component={PendingGroupContainer} />
    </Route>
  </Route>
);
